// ── Dashboard Helpers & Constants ────────────────────────────────────

import type { DebugQuery } from '../types/dashboard'

// ── Number Formatting ────────────────────────────────────────────────

export const fmt = (n: number, style: 'currency' | 'int' | 'decimal' | 'percent' = 'int') => {
    if (style === 'currency') return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n)
    if (style === 'percent') return new Intl.NumberFormat('en-CA', { style: 'percent', maximumFractionDigits: 1 }).format(n)
    if (style === 'decimal') return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(n)
    return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 0 }).format(n)
}

/** Format a Date as YYYY-MM-DD in the LOCAL timezone (avoids UTC shift). */
export const localDateStr = (d: Date = new Date()) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export const pctChange = (curr: number, prev: number) => {
    if (prev === 0) return curr > 0 ? 100 : 0
    return ((curr - prev) / Math.abs(prev)) * 100
}

export const hourLabel = (h: number) => {
    if (h === 0) return '12 AM'
    if (h < 12) return `${h} AM`
    if (h === 12) return '12 PM'
    return `${h - 12} PM`
}

// ── KPI Constants ────────────────────────────────────────────────────

export const KPI_ACCENTS: Record<string, string> = {
    netSales: 'var(--color-primary)',
    margin: 'var(--color-accent)',
    costOfSales: 'var(--color-error)',
    transactions: 'var(--color-info)',
    unitsSold: 'var(--color-success)',
    avgTransaction: 'var(--color-warning)',
    upt: 'var(--color-secondary)',
    discountDepth: 'var(--color-error)',
    sellThrough: 'var(--color-success)',
    inventoryValue: 'var(--color-primary-light)',
    gmroi: 'var(--color-warning)',
}

export const KPI_NO_COMPARISONS = new Set(['sellThrough', 'inventoryValue', 'gmroi'])

// ── SQL Helpers ──────────────────────────────────────────────────────

const SQL_KEYWORDS = /\b(DECLARE|SELECT|FROM|WHERE|AND|OR|INNER|LEFT|RIGHT|FULL|OUTER|JOIN|ON|GROUP|ORDER|BY|HAVING|AS|IN|NOT|NULL|IS|ISNULL|COALESCE|NULLIF|SUM|COUNT|ABS|EXISTS|TOP|DESC|ASC|BETWEEN|LIKE|CASE|WHEN|THEN|ELSE|END|WITH|DISTINCT|DATEPART|RTRIM|LTRIM|SET|DATETIME|INT|VARCHAR|NVARCHAR)\b/gi

export function highlightSql(sql: string): string {
    let s = sql.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    s = s.replace(/'([^']*)'/g, '<span style="color:#22c55e">\'$1\'</span>')
    s = s.replace(SQL_KEYWORDS, (m) => `<span style="color:#a78bfa;font-weight:600">${m.toUpperCase()}</span>`)
    s = s.replace(/@(\w+)/g, '<span style="color:#38bdf8">@$1</span>')
    return s
}

export function buildRunnableSql(q: DebugQuery, index?: number): string {
    let sql = q.sql
    const suffix = index !== undefined ? `_${index}` : ''

    const declares = Object.entries(q.parameters)
        .map(([k, v]) => {
            const newKey = `${k}${suffix}`
            // Replace the parameter in the SQL string, ensuring we match the whole word
            sql = sql.replace(new RegExp(k + '(?![\\w])', 'g'), newKey)

            if (/^-?\d+$/.test(v)) {
                return `DECLARE ${newKey} INT = ${v};`
            }
            if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) {
                return `DECLARE ${newKey} UNIQUEIDENTIFIER = '${v}';`
            }
            if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
                return `DECLARE ${newKey} DATETIME = '${v}';`
            }
            return `DECLARE ${newKey} VARCHAR(255) = '${v}';`
        })
        .join('\n')

    if (!sql.trim().endsWith(';')) {
        sql = sql.trimEnd() + ';'
    }

    return declares ? `${declares}\n\n${sql}` : sql
}
